import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import {
  CanvasElementFrom,
  getRootElementId,
  NavigationContainerVariantType,
  PropsOf,
} from '@blaze-runtime/engine-domain';
import { deepmergeExceptArrays } from '@blaze-shared/shared-util';
import { ThemesService } from '@blaze-shared/theming-data-access';

import { getElementById, getElementEntities } from '../+state/elements/elements.selectors';
import {
  isElementType,
  isNavigationElement,
} from '../canvas-elements/element-model/element-type-guards';
import { getHostSlotIdOfNavigationElement } from '../canvas-elements/element-model/slots';

import { ElementDefinitionsService } from './element-definitions.service';

@Injectable({ providedIn: 'root' })
export class NavElementPropsService {
  private elementDefinitionsService = inject(ElementDefinitionsService);
  private store = inject(Store);
  private themesService = inject(ThemesService);

  /**
   * Observe props of navigation containers based on changes from root element.
   * @param root$
   * @param type
   */
  observeContainerPropsFromRootElement<
    T extends NavigationContainerVariantType = NavigationContainerVariantType,
  >(root$: Observable<CanvasElementFrom<'root'>>, type: T): Observable<PropsOf<T> | undefined> {
    const definition = this.elementDefinitionsService.getDefinition(type);

    return root$.pipe(
      map((root) => {
        if (type === 'navHeaderContainer') {
          return root.slots?.header?.children[0] ?? '';
        }
        if (type === 'navSidebarContainer') {
          return root.slots?.side?.children[0] ?? '';
        }
        if (type === 'navBottombarContainer') {
          return root.slots?.bottom?.children[0] ?? '';
        }
        return undefined;
      }),
      distinctUntilChanged(),
      concatLatestFrom((navElementId) => this.store.select(getElementById(navElementId ?? ''))),
      switchMap(([, navElement]) => {
        if (!isNavigationElement(navElement)) {
          return of(undefined);
        }

        const slotId = getHostSlotIdOfNavigationElement(navElement);

        if (!slotId) {
          return of(undefined);
        }

        return this.store.select(getElementById(navElement.id)).pipe(
          map((freshNavElement) => {
            // Namespace may not be assigned / generated yet
            // case when adding new navigation container.
            // Eventually namespace must be assigned to navigation element.
            const ns = freshNavElement?.slots?.[slotId]?.hosting?.ns ?? '';
            return ns;
          }),
          filter((ns) => !!ns),
          switchMap((ns) => {
            const childRootId = getRootElementId(ns);

            return this.store
              .select(getElementById(childRootId))
              .pipe(map((childRoot) => (isElementType(childRoot, 'root') ? childRoot : undefined)));
          }),
          switchMap((childRoot) => {
            if (!childRoot) {
              return of(undefined);
            }

            return this.store.select(getElementEntities).pipe(
              map((entities) => {
                const navContainerId = childRoot?.slots?.main?.children.find((id) =>
                  isElementType(entities[id], type),
                );
                return navContainerId;
              }),
            );
          }),
          switchMap((navContainerId) => {
            if (!navContainerId) {
              return of(undefined);
            }

            return this.store.select(getElementById(navContainerId)).pipe(
              map((element) => {
                // Simplified props processing for better performance.
                return this.themesService.process(
                  deepmergeExceptArrays(
                    definition?.defaults,
                    (element as CanvasElementFrom<T> | undefined)?.props,
                  ),
                ) as PropsOf<T>;
              }),
            );
          }),
        );
      }),
    );
  }
}
